import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Button, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  TextField,
  Switch,
  FormControlLabel,
  IconButton,
  Tooltip,
  Typography,
  Snackbar
} from '@mui/material';
import { 
  Add as AddIcon, 
  Edit as EditIcon, 
  Delete as DeleteIcon, 
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from '@mui/icons-material';
import { useActivities } from '../../contexts/ActivityContext';
import { supabase } from '../../supabaseClient';
import ColorPicker from '../../components/ColorPicker';

export default function ActivitiesAdminPage() {
  const { 
    activities, 
    fetchActivities, 
    createActivity, 
    updateActivity 
  } = useActivities();

  const [openModal, setOpenModal] = useState(false);
  const [currentActivity, setCurrentActivity] = useState({
    name: '',
    description: '',
    is_active: true,
    color: '#2196F3' // Ajout de la couleur par défaut
  });
  const [isEditing, setIsEditing] = useState(false);
  const [showInactiveActivities, setShowInactiveActivities] = useState(false);
  // IMPORTANT: pointage_count est récupéré via une fonction SQL personnalisée count_pointages_by_activity()
  // Cette fonction retourne un tableau avec activity_id et pointage_count
  // Ne pas supprimer cette colonne car elle est générée dynamiquement côté base de données
  const [orderBy, setOrderBy] = useState('name');
  const [orderDirection, setOrderDirection] = useState('asc');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });

  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);

  const handleOpenCreateModal = () => {
    setCurrentActivity({
      name: '',
      description: '',
      is_active: true,
      color: '#2196F3'
    });
    setIsEditing(false);
    setOpenModal(true);
  };

  const handleOpenEditModal = (activity) => {
    setCurrentActivity(activity);
    setIsEditing(true);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const handleSave = async () => {
    try {
      if (isEditing) {
        await updateActivity(currentActivity.id, {
          name: currentActivity.name,
          description: currentActivity.description,
          is_active: currentActivity.is_active,
          color: currentActivity.color
        });
      } else {
        await createActivity({
          name: currentActivity.name,
          description: currentActivity.description,
          is_active: currentActivity.is_active,
          color: currentActivity.color
        });
      }
      handleCloseModal();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
    }
  };

  const handleDelete = async (id) => {
    try {
      // Vérifier d'abord s'il y a des pointages liés
      const { data: pointagesData, error: checkError } = await supabase
        .from('pointage')
        .select('id')
        .eq('activity', id);

      if (checkError) throw checkError;

      if (pointagesData && pointagesData.length > 0) {
        // S'il y a des pointages liés, désactiver l'activité au lieu de la supprimer
        const { error: updateError } = await supabase
          .from('activities')
          .update({ is_active: false })
          .eq('id', id);

        if (updateError) throw updateError;

        showSnackbar('L\'activité a été désactivée car elle est encore utilisée', 'warning');
      } else {
        // Supprimer si aucun pointage lié
        const { error: deleteError } = await supabase
          .from('activities')
          .delete()
          .eq('id', id);

        if (deleteError) throw deleteError;

        showSnackbar('Activité supprimée avec succès', 'success');
      }

      fetchActivities();
    } catch (error) {
      console.error('Erreur lors de la suppression de l\'activité:', error);
      showSnackbar(error.message || 'Erreur lors de la suppression de l\'activité', 'error');
    }
  };

  const handleSort = (column) => {
    // Si la colonne est déjà triée, inverser la direction
    // Sinon, commencer par un tri ascendant
    if (orderBy === column) {
      setOrderDirection(orderDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setOrderBy(column);
      setOrderDirection('asc');
    }
  };

  const handleToggleActive = async (activity) => {
    try {
      await updateActivity(activity.id, { 
        name: activity.name,
        description: activity.description,
        is_active: !activity.is_active 
      });
    } catch (error) {
      console.error('Erreur lors de la modification du statut:', error);
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({
      open: false,
      message: '',
      severity: 'info'
    });
  };

  return (
    <Box sx={{ width: '100%', padding: 2 }}>
      <Box sx={{ 
        marginBottom: 2 
      }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginBottom: 1 
        }}>
          <Typography variant="h4">Activités</Typography>
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<AddIcon />}
            onClick={handleOpenCreateModal}
          >
            Activité
          </Button>
        </Box>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1 
        }}>
          <Typography>Activités Inactives</Typography>
          <Switch
            checked={showInactiveActivities}
            onChange={() => setShowInactiveActivities(!showInactiveActivities)}
            color="primary"
          />
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell onClick={() => handleSort('name')}>
                Nom
                {orderBy === 'name' && (
                  orderDirection === 'asc' ? <ExpandLessIcon /> : <ExpandMoreIcon />
                )}
              </TableCell>
              <TableCell onClick={() => handleSort('description')}>
                Description
                {orderBy === 'description' && (
                  orderDirection === 'asc' ? <ExpandLessIcon /> : <ExpandMoreIcon />
                )}
              </TableCell>
              <TableCell onClick={() => handleSort('pointage_count')}>
                Nombre de pointages
                {orderBy === 'pointage_count' && (
                  orderDirection === 'asc' ? <ExpandLessIcon /> : <ExpandMoreIcon />
                )}
              </TableCell>
              <TableCell>Couleur</TableCell>
              <TableCell onClick={() => handleSort('is_active')}>
                Statut
                {orderBy === 'is_active' && (
                  orderDirection === 'asc' ? <ExpandLessIcon /> : <ExpandMoreIcon />
                )}
              </TableCell>
              <TableCell align="left">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {activities
              .sort((a, b) => {
                // Trier d'abord par le critère de tri sélectionné
                if (orderBy === 'name') {
                  return orderDirection === 'asc' ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name);
                } else if (orderBy === 'description') {
                  return orderDirection === 'asc' ? a.description.localeCompare(b.description) : b.description.localeCompare(a.description);
                } else if (orderBy === 'pointage_count') {
                  return orderDirection === 'asc' ? a.pointage_count - b.pointage_count : b.pointage_count - a.pointage_count;
                } else if (orderBy === 'is_active') {
                  return orderDirection === 'asc' 
                    ? (a.is_active === b.is_active ? 0 : (a.is_active ? -1 : 1))
                    : (a.is_active === b.is_active ? 0 : (a.is_active ? 1 : -1));
                }
                return 0;
              })
              .filter(activity => showInactiveActivities || activity.is_active)
              .map((activity) => (
                <TableRow 
                  key={activity.id} 
                  sx={{ 
                    backgroundColor: activity.is_active ? 'inherit' : '#f5f5f5',
                    opacity: activity.is_active ? 1 : 0.7
                  }}
                >
                  <TableCell>{activity.name}</TableCell>
                  <TableCell>{activity.description}</TableCell>
                  <TableCell>{activity.pointage_count}</TableCell>
                  <TableCell style={{ 
                    backgroundColor: activity.color || '#2196F3',
                    width: '50px' 
                  }}></TableCell>
                  <TableCell>
                    <Switch
                      checked={activity.is_active}
                      onChange={() => handleToggleActive(activity)}
                      color="primary"
                    />
                  </TableCell>
                  <TableCell align="left">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Tooltip title="Modifier">
                        <IconButton 
                          color="primary" 
                          onClick={() => handleOpenEditModal(activity)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Supprimer">
                        <IconButton 
                          color="error" 
                          onClick={() => handleDelete(activity.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            }
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openModal} onClose={handleCloseModal}>
        <DialogTitle>
          {isEditing ? 'Modifier l\'activité' : 'Créer une nouvelle activité'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Nom de l'activité"
            fullWidth
            value={currentActivity.name}
            onChange={(e) => setCurrentActivity(prev => ({
              ...prev, 
              name: e.target.value
            }))}
          />
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            multiline
            rows={3}
            value={currentActivity.description}
            onChange={(e) => setCurrentActivity(prev => ({
              ...prev, 
              description: e.target.value
            }))}
          />
          <FormControlLabel
            control={
              <Switch
                checked={currentActivity.is_active}
                onChange={(e) => setCurrentActivity(prev => ({
                  ...prev, 
                  is_active: e.target.checked
                }))}
                color="primary"
              />
            }
            label="Actif"
          />
          <Box sx={{ mt: 2 }}>
            <ColorPicker
              value={currentActivity.color}
              onChange={(color) => setCurrentActivity(prev => ({
                ...prev,
                color: color
              }))}
              label="Couleur"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} color="secondary">
            Annuler
          </Button>
          <Button onClick={handleSave} color="primary">
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        message={snackbar.message}
        severity={snackbar.severity}
      />
    </Box>
  );
}
